<script setup>
import { computed, ref } from 'vue'
import { useForm, usePage } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  topic:          { type: String, default: 'general' },
  vehicleId:      { type: [Number, String], default: null },
  prefillMessage: { type: String, default: '' }
})

const { t } = useI18n()
const page = usePage()
const inquirePath = computed(() => page.props.inquire_path || '/inquire')

const form = useForm({
  name:       '',
  email:      '',
  phone:      '',
  message:    props.prefillMessage,
  topic:      props.topic,
  vehicle_id: props.vehicleId,
  website:    ''   // honeypot
})

// Client-side validation so users get inline feedback before the round-trip.
// Server-side flash still wins for anything we can't catch here (rate limit,
// Chatwoot down, etc.) — PublicLayout renders flash.alert as a banner.
const localErrors = ref({})

function validate() {
  const errs = {}
  if (!form.message?.trim()) {
    errs.message = t('public.inquire.errors.message_required')
  }
  if (!form.email?.trim() && !form.phone?.trim()) {
    errs.contact = t('public.inquire.errors.contact_required')
  }
  localErrors.value = errs
  return Object.keys(errs).length === 0
}

function submit() {
  if (!validate()) return
  form.transform((d) => ({ inquiry: d })).post(inquirePath.value, {
    preserveScroll: true,
    onSuccess: () => { localErrors.value = {} }
  })
}
</script>

<template>
  <form class="iform" @submit.prevent="submit">
    <div class="iform__row">
      <label class="iform__field">
        <span class="iform__label">{{ t('public.inquire.fields.name') }}</span>
        <input v-model="form.name" type="text" class="iform__input" />
      </label>
    </div>
    <div class="iform__row iform__row--two">
      <label class="iform__field">
        <span class="iform__label">{{ t('public.inquire.fields.email') }}</span>
        <input v-model="form.email" type="email" autocomplete="email" class="iform__input" :class="{ 'iform__input--err': localErrors.contact }" />
      </label>
      <label class="iform__field">
        <span class="iform__label">{{ t('public.inquire.fields.phone') }}</span>
        <input v-model="form.phone" type="tel" autocomplete="tel" class="iform__input" :class="{ 'iform__input--err': localErrors.contact }" />
      </label>
    </div>
    <p v-if="localErrors.contact" class="iform__err">{{ localErrors.contact }}</p>

    <label class="iform__field">
      <span class="iform__label">{{ t('public.inquire.fields.message') }}</span>
      <textarea v-model="form.message" rows="4" class="iform__input" :class="{ 'iform__input--err': localErrors.message }"></textarea>
      <p v-if="localErrors.message" class="iform__err">{{ localErrors.message }}</p>
    </label>

    <!-- Honeypot — visible only to bots -->
    <label class="iform__hp" aria-hidden="true">
      Website
      <input v-model="form.website" type="text" tabindex="-1" autocomplete="off" />
    </label>

    <button type="submit" class="iform__submit" :disabled="form.processing">
      {{ form.processing ? t('public.inquire.sending') : t('public.inquire.submit') }}
    </button>
    <p class="iform__hint">{{ t('public.inquire.hint') }}</p>
  </form>
</template>

<style scoped>
.iform { display: grid; gap: 0.875rem; max-width: 36rem; }
.iform__row--two { display: grid; gap: 0.875rem; grid-template-columns: 1fr; }
@media (min-width: 540px) { .iform__row--two { grid-template-columns: 1fr 1fr; } }
.iform__field { display: grid; gap: 0.3rem; }
.iform__label { font-size: 0.8125rem; color: var(--inkSoft); }
.iform__input {
  width: 100%; padding: 0.55rem 0.75rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 0.5rem; color: var(--ink); font-size: 0.9375rem;
}
.iform__input:focus { outline: 2px solid var(--accent); outline-offset: 1px; border-color: transparent; }
.iform__input--err { border-color: var(--danger); }
.iform__err { margin: 0; font-size: 0.8125rem; color: var(--danger); }
.iform__hp { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.iform__submit {
  justify-self: start;
  padding: 0.65rem 1.25rem; border-radius: 0.625rem; border: 0;
  background: var(--accent); color: var(--accentInk);
  font-weight: 600; cursor: pointer;
}
.iform__submit:disabled { opacity: 0.6; cursor: progress; }
.iform__hint { font-size: 0.75rem; color: var(--muted); margin: 0; }
</style>
